import { LengthRange } from './lengthRange.gen';

export enum TickEnum {
	Label,
	Long,
	Short,
}

export interface RulerTick {
	tickDepthMetres: number;
	type: TickEnum;
}

export const getTicksPerMetre = (currentScalePixelsPerMetre: number): number => {
	if (currentScalePixelsPerMetre < 1) {
		return 0.05;
	} else if (currentScalePixelsPerMetre < 2) {
		return 0.1;
	} else if (currentScalePixelsPerMetre < 5) {
		return 0.2;
	} else if (currentScalePixelsPerMetre < 10) {
		return 0.5;
	} else if (currentScalePixelsPerMetre < 20) {
		return 1;
	} else if (currentScalePixelsPerMetre < 50) {
		return 2;
	} else if (currentScalePixelsPerMetre < 100) {
		return 5;
	} else if (currentScalePixelsPerMetre < 200) {
		return 10;
	} else if (currentScalePixelsPerMetre < 500) {
		return 20;
	}

	return 50;
};

export const getTickLabelsPerMetre = (currentScalePixelsPerMetre: number, frequency: number): number => {
	if (currentScalePixelsPerMetre < 1) {
		return 0.05 / frequency;
	} else if (currentScalePixelsPerMetre < 2) {
		return 0.1 / frequency;
	} else if (currentScalePixelsPerMetre < 5) {
		return 0.2 / frequency;
	} else if (currentScalePixelsPerMetre < 10) {
		return 0.5 / frequency;
	} else if (currentScalePixelsPerMetre < 20) {
		return 1 / frequency;
	} else if (currentScalePixelsPerMetre < 50) {
		return 2 / frequency;
	} else if (currentScalePixelsPerMetre < 100) {
		return 5 / frequency;
	} else if (currentScalePixelsPerMetre < 200) {
		return 10 / frequency;
	} else if (currentScalePixelsPerMetre < 500) {
		return 20 / frequency;
	} else if (currentScalePixelsPerMetre < 1000) {
		return 50 / frequency;
	}

	return 100 / frequency;
};

interface BuildTickMarksProps {
	pxPerM: number;
	rulerRangeM: LengthRange | undefined;
	labelFrequencyTicks: number;
}

const buildRulerTicks = (
	rulerVirtualRange: LengthRange,
	ticksPerMetre: number,
	tickLabelsPerMetre: number,
): RulerTick[] =>
	rulerVirtualRange.depthTo - rulerVirtualRange.depthFrom > 0
		? [...Array(Math.ceil((rulerVirtualRange.depthTo - rulerVirtualRange.depthFrom) * ticksPerMetre)).keys()].map(
				(i) => ({
					// Round to 2dp (When necessary)
					tickDepthMetres:
						Math.round((rulerVirtualRange.depthFrom + i / ticksPerMetre + Number.EPSILON) * 100) / 100,
					type:
						i % (ticksPerMetre / tickLabelsPerMetre) === 0
							? TickEnum.Label
							: i % (ticksPerMetre / tickLabelsPerMetre / 2) === 0
								? TickEnum.Long
								: TickEnum.Short,
				}),
			)
		: [];

const getVisibleTicks = (allRulerTicks: RulerTick[], tickVirtualRangeMetres: LengthRange): RulerTick[] =>
	allRulerTicks.filter(
		(t) =>
			t.tickDepthMetres > tickVirtualRangeMetres.depthFrom && t.tickDepthMetres < tickVirtualRangeMetres.depthTo,
	);

export const getVisibleTickMarks = ({ pxPerM, rulerRangeM, labelFrequencyTicks }: BuildTickMarksProps): RulerTick[] => {
	const ticksPerMetre = getTicksPerMetre(pxPerM);
	const tickLabelsPerMetre = getTickLabelsPerMetre(pxPerM, labelFrequencyTicks);
	const rulerVirtualRange: LengthRange = {
		depthFrom: Math.floor(rulerRangeM?.depthFrom ?? 0),
		depthTo: Math.ceil(rulerRangeM?.depthTo ?? 0),
	};
	const allTicks = buildRulerTicks(rulerVirtualRange, ticksPerMetre, tickLabelsPerMetre);

	return getVisibleTicks(allTicks, {
		depthFrom: rulerRangeM?.depthFrom ?? 0,
		depthTo: rulerRangeM?.depthTo ?? 0,
	});
};
